import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { Paper } from '@gogovan/entier-web-core/ext/mui/Paper';
import { TextField } from '@gogovan/entier-web-core/ext/mui/TextField';
import { InputAdornment } from '@gogovan/entier-web-core/ext/mui/InputAdornment';
import { IconButton } from '@gogovan/entier-web-core/ext/mui/IconButton';
import { useLoginMutation } from '@/modules/session/auth.api';
import { useProfileLoginMutation } from '@/modules/session/auth-profile.api';
import { Icon } from '@gogovan/entier-web-core/ext/mui/Icon';
import { Grid } from '@gogovan/entier-web-core/ext/mui/Grid';
import { LanguageSelection } from '@/utilities/reusable-components/language-selection/language-selection';
import { LoadingButton } from '@gogovan/entier-web-core/components/loading-button';
import { Alert } from '@gogovan/entier-web-core/ext/mui/Alert';
import { AlertTitle } from '@gogovan/entier-web-core/ext/mui/AlertTitle';
import { AUTH_STATE, selectAuthState } from '@/modules/session/auth.slice';
import { useFeatureFlag } from '@gogovan/entier-web-core/components/feature-flag';
import FEATURE_FLAGS from '@/modules/feature-flag/feature-flag.enum';
import { DateTime } from '@gogovan/entier-web-core/ext/luxon';
import { Container, GogoxIMG, paperStyles, textFieldStyles, buttonStyles } from './login.styles';

const LoginPage = () => {
  const isAuthLoading = useSelector(selectAuthState) === AUTH_STATE.LOADING;
  const { t } = useTranslation('common');

  const [errorMessage, setErrorMessage] = useState(undefined);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(null);

  const [isFormSubmitting, setFormIsSubmitting] = useState(false);

  const { value: isProfileAuthEnabled } = useFeatureFlag([FEATURE_FLAGS.USE_PROFILE_AUTH_ENDPOINT]);
  const [login] = useLoginMutation();
  const [profileLogin] = useProfileLoginMutation();

  const onUsernameChange = (event) => {
    if (errorMessage) setErrorMessage(undefined);
    setUsername(event.target.value);
  };

  const onPasswordChange = (event) => {
    if (errorMessage) setErrorMessage(undefined);
    setPassword(event.target.value);
  };

  // Effect to handle lockout status and reset it when the lockout period expires
  useEffect(() => {
    if (isLockedOut && lockoutTime) {
      const checkLockoutStatus = () => {
        const now = DateTime.now();
        if (now > lockoutTime) {
          setIsLockedOut(false);
          setLockoutTime(null);
        }
      };

      // Check immediately
      checkLockoutStatus();

      // Set up an interval to periodically check if the lockout period has expired
      const interval = setInterval(checkLockoutStatus, 5000); // Check every 5 seconds

      return () => {
        clearInterval(interval);
      };
    }
    return undefined;
  }, [isLockedOut, lockoutTime]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setFormIsSubmitting(true);
      if (isProfileAuthEnabled) {
        await profileLogin({ username, password }).unwrap();
      } else {
        await login({ username, password }).unwrap();
      }
    } catch (error) {
      if (error.status === 429) {
        setErrorMessage(undefined);
        setIsLockedOut(true);
        setLockoutTime(DateTime.fromISO(error.data?.payload));
        return;
      }
      setErrorMessage(error.data.payload);
    } finally {
      setFormIsSubmitting(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <form onSubmit={onSubmit} noValidate method='POST'>
      <Container>
        <Grid>
          <Paper sx={paperStyles} elevation={3} data-testid='login-paper'>
            <GogoxIMG data-testid='gogox-logo' />

            {isLockedOut && lockoutTime?.isValid && (
              <Alert severity='error' sx={textFieldStyles}>
                <AlertTitle>{t('account_temporarily_locked_too_many_failed_attempts')}</AlertTitle>
                {`${t('please_try_again_after')} ${lockoutTime.toFormat('yyyy-MM-dd HH:mm:ss')}`}
              </Alert>
            )}

            <TextField
              sx={textFieldStyles}
              error={errorMessage !== undefined}
              label={t('username')}
              data-testid='username-input'
              onChange={onUsernameChange}
              type='text'
              value={username}
              disabled={isLockedOut}
            />
            <TextField
              sx={textFieldStyles}
              error={errorMessage !== undefined}
              helperText={errorMessage}
              label={t('password')}
              data-testid='password-input'
              onChange={onPasswordChange}
              type={showPassword ? 'text' : 'password'}
              value={password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton onClick={handleClickShowPassword}>
                      <Icon>{showPassword ? 'visibility' : 'visibility_off'}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
                autoComplete: 'off',
              }}
              disabled={isLockedOut}
            />
            <LoadingButton
              type='submit'
              variant='contained'
              data-testid='signin-button'
              sx={buttonStyles}
              isLoading={isAuthLoading || isFormSubmitting}
              fullWidth
              disabled={isLockedOut}
            >
              {t('sign_in')}
            </LoadingButton>
          </Paper>
          <LanguageSelection />
        </Grid>
      </Container>
    </form>
  );
};

export default LoginPage;
